import type { ReactElement } from 'react';
import type { ShadowAiProcessAncestor } from '@kirin/api-client/contracts/shadow-ai';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * A PID exactly as the rest of the product and the OS write it.
 *
 * Not zero-padded: `ps` right-aligns with spaces, `00000` reads as octal, and a
 * padded value does not match what the user would paste into `kill` or `ps -p`,
 * nor the unpadded PID shown elsewhere in the app. The block-column look comes
 * from `tabular-nums` and right alignment in the cell instead.
 */
function formatPid(pid: number | null | undefined): string {
  return pid == null ? '—' : String(pid);
}

/**
 * Best available description of what an ancestor is running.
 *
 * argv first, because that is what answers "what launched this" — `/bin/zsh`
 * does not distinguish a login shell from a script runner, `-zsh` does. The
 * fallbacks are not defensive padding: an unprivileged daemon routinely cannot
 * read a root-owned ancestor's argv, and the agent reports a field it could not
 * read as absent rather than guessing.
 */
function describeAncestor(node: ShadowAiProcessAncestor): string {
  return node.commandLine || node.binaryPath || node.name || '—';
}

/**
 * The flagged process's lineage, as the flat table the design calls for
 * (KIR-4651).
 *
 * Root-first, which is the reverse of the wire order: the chain arrives
 * detected-process-first, matching the order the agent's walk produces, and
 * flipping it upstream would silently diverge the API from the package it comes
 * from. Display order is a presentation concern, so it is reversed here and
 * only here.
 */
export function ProcessAncestryTable({
  ancestry,
  detectedPid,
}: {
  ancestry: ShadowAiProcessAncestor[];
  detectedPid: number;
}): ReactElement | null {
  if (ancestry.length === 0) return null;

  const rootFirst = [...ancestry].reverse();

  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium">Process ancestry</div>

      {/*
        `table-fixed` is load-bearing, not cosmetic. Under the default auto
        layout the COMMAND column sizes to its content, and a packed node argv
        is thousands of characters — the table then outgrows the panel, the
        `truncate` below never engages, and the DETECTED badge is pushed off
        the right edge where no one sees it. Fixed layout bounds the column to
        the panel so the text ellipsises and the badge stays put; the full
        value is still one hover away in the tooltip.

        Table supplies its own `overflow-x-auto` wrapper, so adding one here
        would nest a second scroller inside the first.
      */}
      <Table className="table-fixed text-xs">
        <TableHeader>
          <TableRow>
            <TableHead className="h-7 w-20 font-mono text-right">PID</TableHead>
            <TableHead className="h-7 w-20 font-mono text-right">
              PPID
            </TableHead>
            <TableHead className="h-7 w-32">USER</TableHead>
            <TableHead className="h-7">COMMAND</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rootFirst.map((node, index) => {
            const isDetected = node.pid === detectedPid;
            const command = describeAncestor(node);
            return (
              <TableRow
                // PIDs are unique within one chain, but a racy read can
                // repeat one; the index keeps the key unique regardless.
                key={`${node.pid}-${index}`}
                className={cn(isDetected && 'bg-muted/50')}
              >
                <TableCell className="py-1 font-mono text-right tabular-nums">
                  {formatPid(node.pid)}
                </TableCell>
                <TableCell className="py-1 font-mono text-right tabular-nums text-muted-foreground">
                  {formatPid(node.ppid)}
                </TableCell>
                <TableCell className="py-1 truncate">
                  {node.user || '—'}
                </TableCell>
                <TableCell className="py-1 min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <code
                          className={cn(
                            'font-mono truncate block cursor-default',
                            isDetected && 'text-primary font-medium'
                          )}
                        >
                          {command}
                        </code>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className="max-w-lg break-all font-mono"
                      >
                        {command}
                      </TooltipContent>
                    </Tooltip>
                    {isDetected && (
                      <Badge
                        variant="secondary"
                        className="text-xs px-1 py-0 shrink-0"
                      >
                        DETECTED
                      </Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <p className="text-xs text-muted-foreground">
        Each row shows PID, PPID, user and command. This is most useful when a
        node process launches an MCP server: the chain shows what really started
        it.
      </p>
    </div>
  );
}
